import { memo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonOutlined from "@mui/icons-material/PersonOutlined";
import { RoutingMetadata } from "src/views/assistant/routing/types";
import { getModeDisplayName } from "src/views/assistant/routing/routingDisplayUtils";
import { useGetConfigBySlugName } from "src/views/assistant/AssistantServices";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import NoContentCard from "../../NoContentCard";

interface RoutingTabProps {
  routing: RoutingMetadata | null;
}

/**
 * Shared persona details section used by both routing and manual selection views.
 */
const PersonaDetailsSection = ({
  slug,
}: {
  slug: string;
}) => {
  const theme = useTheme();
  const { data: personaDetails, isLoading: detailsLoading } =
    useGetConfigBySlugName(slug);
  const [promptExpanded, setPromptExpanded] = useState(false);

  if (detailsLoading) {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Loading agent details…
        </Typography>
      </Stack>
    );
  }

  if (!personaDetails) {
    return (
      <Typography variant="caption" color="text.secondary">
        Agent details not found
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {/* Description */}
      {personaDetails.description && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Description
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {personaDetails.description}
          </Typography>
        </Box>
      )}

      {/* System Prompt */}
      {personaDetails.systemPrompt && (
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ cursor: "pointer", mb: 0.5 }}
            onClick={() => setPromptExpanded((prev) => !prev)}
          >
            <Typography variant="subtitle2">System Prompt</Typography>
            {promptExpanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </Stack>
          <Collapse in={promptExpanded}>
            <Box
              sx={{
                maxHeight: 200,
                overflow: "auto",
                p: 1,
                borderRadius: 1,
                backgroundColor: theme.palette.action.hover,
                fontFamily: "monospace",
                fontSize: "0.75rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {personaDetails.systemPrompt}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Datasets */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Datasets
        </Typography>
        {personaDetails.datasets?.length > 0 ? (
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {personaDetails.datasets.map(
              (dataset: { name: string }, idx: number) => (
                <Chip
                  key={idx}
                  label={dataset.name}
                  size="small"
                  variant="outlined"
                />
              ),
            )}
          </Stack>
        ) : (
          <Typography variant="caption" color="text.secondary">
            No datasets
          </Typography>
        )}
      </Box>

      {/* Model */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Model
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {personaDetails.modelId ?? "Not configured"}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {personaDetails.modelCapabilities?.supportsVision && (
            <Chip label="Vision" size="small" variant="outlined" />
          )}
          {personaDetails.modelCapabilities?.supportsReasoning && (
            <Chip label="Reasoning" size="small" variant="outlined" />
          )}
          {personaDetails.modelCapabilities?.supportsFunctionCalling && (
            <Chip label="Tool Calling" size="small" variant="outlined" />
          )}
        </Stack>
      </Box>

      {/* Config */}
      {(personaDetails.topSearchResults != null ||
        personaDetails.toolCalling != null) && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Config
          </Typography>
          {personaDetails.topSearchResults != null && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Top search results: {personaDetails.topSearchResults}
            </Typography>
          )}
          {personaDetails.toolCalling != null && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Tool calling:{" "}
              {personaDetails.toolCalling ? "Enabled" : "Disabled"}
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );
};

const RoutingTab = memo(({ routing }: RoutingTabProps) => {
  const theme = useTheme();
  const { persona } = useChatStore();

  // Determine if a persona was manually selected (no routing metadata yet)
  const manualSlug =
    !routing && persona?.slug
      ? persona.slug
      : null;
  const manualName = manualSlug ? persona?.name : null;

  if (!routing && !manualSlug) {
    return (
      <NoContentCard
        title="No routing data"
        description="Routing metadata will appear here when intelligent routing is active or an agent is selected."
      />
    );
  }

  // Slug to look up details for — either from routing or manual selection
  const detailsSlug = routing?.persona_slug ?? manualSlug ?? "";

  const confidencePct = routing ? Math.round(routing.confidence * 100) : null;

  return (
    <Box data-testid="routing-tab" sx={{ p: 2, overflow: "auto" }}>
      {/* Persona */}
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Agent
        </Typography>
        {routing ? (
          <Chip
            data-testid="routing-tab-persona-chip"
            label={getModeDisplayName(routing)}
            size="small"
            color={routing.access_denied ? "warning" : "primary"}
            variant="filled"
          />
        ) : (
          <Chip
            data-testid="routing-tab-manual-chip"
            label={manualName || "Custom"}
            size="small"
            variant="outlined"
            icon={<PersonOutlined sx={{ fontSize: 14 }} />}
          />
        )}
      </Stack>

      {/* Access denied banner */}
      {routing?.access_denied && (
        <Alert severity="warning" sx={{ mb: 1.5 }} variant="outlined">
          <Typography variant="body2">
            {routing.restriction_message || "This workflow requires a license upgrade."}
          </Typography>
        </Alert>
      )}

      {/* Confidence (auto-routed only) */}
      {confidencePct != null && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Confidence
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ mb: 1.5 }}
          >
            <LinearProgress
              data-testid="routing-tab-confidence"
              variant="determinate"
              value={confidencePct}
              sx={{
                flex: 1,
                height: 6,
                borderRadius: 3,
                backgroundColor: theme.palette.action.hover,
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                },
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flexShrink: 0 }}
            >
              {confidencePct}%
            </Typography>
          </Stack>
        </>
      )}

      {/* Reasoning (auto-routed) or "Manually selected" indicator */}
      {routing?.reasoning ? (
        <>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Reasoning
          </Typography>
          <Typography data-testid="routing-tab-reasoning" variant="body2" color="text.secondary">
            {routing.reasoning}
          </Typography>
        </>
      ) : (
        !routing && (
          <Typography variant="body2" color="text.secondary">
            Manually selected agent — automatic routing is disabled.
          </Typography>
        )
      )}

      {/* Persona Details */}
      <Divider sx={{ my: 2 }} />
      <PersonaDetailsSection slug={detailsSlug} />
    </Box>
  );
});

RoutingTab.displayName = "RoutingTab";

export default RoutingTab;
